/**
 * IDs in agent-written text. Employees name their work by short IDs
 * ("question d2de109f", "job e1538ae3"). These pure helpers find such tokens,
 * map a resolved match to the page that shows it, and turn the tokens that
 * resolved into links inside a markdown tree. The server side is
 * `system/services/id_lookup.py`; the lookup client is `api/lookup.ts`.
 */
import type { IdKind, IdMatch } from "../api/lookup";
import { agentDrawerUrl, reviewUrl } from "./aiTeam";
import { waveUrl } from "./console";

/** An 8-hex-digit short ID or a full UUID, lowercase, standing alone. */
const ID_TOKEN_RE = /\b[0-9a-f]{8}(?:-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})?\b/g;
/** One lookup request carries at most this many IDs (server `LOOKUP_MAX`). */
export const LOOKUP_MAX = 50;

export const KIND_LABEL: Record<IdKind, string> = {
  question: "Question",
  job: "Job",
  review: "Review",
  wave: "Outreach wave",
  goal: "Goal",
  task: "Task",
};

/** Is this the start of a UUID with at least eight hex digits? Mirrors the server's `normalize_id_prefix`. */
export function isIdPrefix(value: string): boolean {
  return /^[0-9a-f]{8}(?:-[0-9a-f]{0,4}(?:-[0-9a-f]{0,4}(?:-[0-9a-f]{0,4}(?:-[0-9a-f]{0,12})?)?)?)?$/.test(value);
}

/** Every distinct ID token in a text, in order of appearance. */
export function findIdTokens(text: string | null | undefined): string[] {
  if (!text) return [];
  const seen = new Set<string>();
  for (const m of text.matchAll(ID_TOKEN_RE)) {
    seen.add(m[0]);
    if (seen.size >= LOOKUP_MAX) break;
  }
  return [...seen];
}

/** The page that shows a match, or null when it has none (e.g. an operator's own job). */
export function matchUrl(m: IdMatch): string | null {
  switch (m.kind) {
    case "question":
      return `/team/questions?id=${m.id}`;
    case "job":
      return m.scope_type === "agent" && m.scope_id
        ? agentDrawerUrl(m.scope_id, { tab: "jobs", jobId: m.id })
        : null;
    case "review":
      return reviewUrl(m.id, m.review_kind);
    case "wave":
      return waveUrl(m.id);
    case "goal":
      return m.agent_id ? agentDrawerUrl(m.agent_id, { tab: "goals" }) : "/team";
    case "task":
      return `/tasks?task=${m.id}`;
  }
}

/** Where a token links to: the page itself for one match, the chooser for several. */
export function tokenHref(token: string, matches: IdMatch[] | undefined): string | null {
  if (!matches?.length) return null;
  if (matches.length === 1) return matchUrl(matches[0]) ?? `/go/${token}`;
  return `/go/${token}`;
}

// ---------------------------------------------------------------------------
// Markdown: link the tokens that resolved
// ---------------------------------------------------------------------------

/** The slice of an mdast node this plugin reads and writes. */
interface MdNode {
  type: string;
  value?: string;
  url?: string;
  children?: MdNode[];
}

/** A text node's value split into text and link nodes for the known tokens. */
export function splitIdText(value: string, links: ReadonlyMap<string, string>): MdNode[] {
  const out: MdNode[] = [];
  let last = 0;
  for (const m of value.matchAll(ID_TOKEN_RE)) {
    const href = links.get(m[0]);
    const at = m.index ?? 0;
    if (!href) continue;
    if (at > last) out.push({ type: "text", value: value.slice(last, at) });
    out.push({ type: "link", url: href, children: [{ type: "text", value: m[0] }] });
    last = at + m[0].length;
  }
  if (last === 0) return [{ type: "text", value }];
  if (last < value.length) out.push({ type: "text", value: value.slice(last) });
  return out;
}

function linkIds(node: MdNode, links: ReadonlyMap<string, string>): void {
  if (!node.children || node.type === "link" || node.type === "linkReference") return;
  const out: MdNode[] = [];
  for (const child of node.children) {
    if (child.type === "text" && child.value) {
      out.push(...splitIdText(child.value, links));
    } else if (child.type === "inlineCode" && child.value && links.has(child.value.trim())) {
      out.push({ type: "link", url: links.get(child.value.trim()), children: [child] });
    } else {
      linkIds(child, links);
      out.push(child);
    }
  }
  node.children = out;
}

/** Remark plugin: IDs that resolved (`links`: token → href) become links; existing links stay as they are. */
export function remarkIdLinks(options: { links: ReadonlyMap<string, string> }) {
  return (tree: MdNode) => {
    if (options.links.size) linkIds(tree, options.links);
  };
}
